import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  $addUpdateTag,
  $createParagraphNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  SKIP_SELECTION_FOCUS_TAG,
  LexicalEditor,
  $isNodeSelection,
  $isElementNode,
  LexicalNode,
  $findMatchingParent,
  $isRootOrShadowRoot,
  RangeSelection,
  TextNode,
  ElementNode,
  HISTORIC_TAG,
  SKIP_DOM_SELECTION_TAG,
  LexicalCommand,
  createCommand,
  COMMAND_PRIORITY_EDITOR,
  BaseSelection,
  $isParagraphNode,
  ParagraphNode,
  KEY_TAB_COMMAND,
  INSERT_TAB_COMMAND
} from 'lexical';
import {
  $createHeadingNode,
  $isHeadingNode,
  HeadingTagType
} from '@lexical/rich-text'

import { $getSelectionStyleValueForProperty, $isAtNodeEnd, $patchStyleText, $setBlocksType } from '@lexical/selection';

import {
  blockTypeToBlockName,
  useToolbarState,
  lineHeights
} from './ToolbarContext';

import { createPortal, useCallback, useEffect, useRef, useState } from 'react';

function Divider() {
  return <div className="divider" />;
}

import { RiBold, RiUnderline, RiItalic, RiLineHeight, RiFontColor, RiArrowGoBackFill, RiArrowGoForwardFill, RiArrowDownSLine, RiFormula } from "react-icons/ri";

import DropDown, { DropDownItem } from '../components/dropdown';
import EquationEditorPopup from '../components/EquationEditorPopup';
import { INSERT_EQUATION_COMMAND } from './EquationPlugin';
import { EquationNode } from '../nodes/EquationNode';
import { CustomParagraphNode } from '../nodes/CustomParagraph';
import ColorPicker from '../components/ColorPicker';
function dropDownActiveClass(active: boolean) {
  if (active) {
    return 'active dropdown-item-active';
  } else {
    return '';
  }
}

export const formatParagraph = (editor: LexicalEditor) => {
  editor.update(() => {
    $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
    const selection = $getSelection();
    $setBlocksType(selection, () => $createParagraphNode());
  });
};


export const formatHeading = (
  editor: LexicalEditor,
  blockType: string,
  headingSize: HeadingTagType,
) => {
  if (blockType !== headingSize) {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
      const selection = $getSelection();
      $setBlocksType(selection, () => $createHeadingNode(headingSize));
    });
  }
};

function $findAllParagraphsInSelection(selection: BaseSelection | null) {
  const paragraphsInSelection = new Set<ParagraphNode>();
  if ($isRangeSelection(selection)) {
    const selectedNodes = selection.getNodes();

    for (const node of selectedNodes) {
      // Check if the node itself is a paragraph
      if ($isParagraphNode(node)) {
        paragraphsInSelection.add(node);
      } else {
        const parent = node.getParent();
        if (parent && $isParagraphNode(parent)) {
          paragraphsInSelection.add(parent);
        }
      }
    }
    return paragraphsInSelection;
  }
}


function $findTopLevelElement(node: LexicalNode) {
  let topLevelElement =
    node.getKey() === 'root'
      ? node
      : $findMatchingParent(node, (e) => {
        const parent = e.getParent();
        return parent !== null && $isRootOrShadowRoot(parent);
      });

  if (topLevelElement === null) {
    topLevelElement = node.getTopLevelElementOrThrow();
  }
  return topLevelElement;
}

export const EDIT_EQUATION_COMMAND: LexicalCommand<EquationNode> = createCommand(
  'EDIT_EQUATION_COMMAND',
);

type Props = {
  type: 'none' | 'basic',
  showEquationEditorButton: boolean,
  equationEditorContainerElement?: HTMLElement
}

export default function ToolbarPlugin(props: Props) {

  if (props.showEquationEditorButton && !props.equationEditorContainerElement) {
    throw "equationEditorContainerElement can not be null when showEquationEditorButton is true"
  }
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const { toolbarState, updateToolbarState } = useToolbarState();
  const [fontColorDropDownOpen, setFontColorDropDownOpen] = useState(false);

  const $handleHeadingNode = useCallback((selectedElement: LexicalNode) => {
    let type = $isHeadingNode(selectedElement)
      ? selectedElement.getTag()
      : selectedElement.getType();

    if (type == "custom-paragraph") {
      type = "paragraph";
    }

    if (type in blockTypeToBlockName) {
      updateToolbarState(
        'blockType',
        type as keyof typeof blockTypeToBlockName,
      );
    }
  }, [updateToolbarState]);

  const $handleParagraphLineHeight = useCallback((selectedElement: LexicalNode) => {
    if(!(selectedElement instanceof CustomParagraphNode)) {
      return;
    }

    if(selectedElement.__lineHeight) {
      updateToolbarState('lineHeight', selectedElement.__lineHeight);
    } else {
      updateToolbarState('lineHeight', 'default');
    }
  }, [updateToolbarState]);

  const applyStyleText = useCallback(
    (
      styles: Record<string, string | null>,
      skipHistoryStack?: boolean,
      skipRefocus: boolean = false,
    ) => {
      editor.update(
        () => {
          if (skipRefocus) {
            $addUpdateTag(SKIP_DOM_SELECTION_TAG);
          }
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: HISTORIC_TAG } : {},
      );
    },
    [editor],
  );

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = $findTopLevelElement(anchorNode);
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        $handleHeadingNode(element);
        $handleParagraphLineHeight(element);
      }

      updateToolbarState(
        'fontColor',
        $getSelectionStyleValueForProperty(selection, 'color', 'rgb(0, 0, 0)'),
      );
    }
    if ($isRangeSelection(selection)) {
      // Update text format
      updateToolbarState('isBold', selection.hasFormat('bold'));
      updateToolbarState('isItalic', selection.hasFormat('italic'));
      updateToolbarState('isUnderline', selection.hasFormat('underline'));
    }
    if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes();
      for (const selectedNode of nodes) {
        const selectedElement = $findTopLevelElement(selectedNode);
        $handleHeadingNode(selectedElement);
        $handleParagraphLineHeight(selectedElement);
      }
    }
  }, [
    editor,
    updateToolbarState,
    $handleHeadingNode,
    $handleParagraphLineHeight
  ]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(
          () => {
            $updateToolbar();
          },
          { editor },
        );
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          updateToolbarState("canUndo", payload)
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          updateToolbarState("canRedo", payload)
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        EDIT_EQUATION_COMMAND,
        (payload) => {
          updateToolbarState("equationEditorEditNode", payload)
          updateToolbarState("equationEditorIsOpen", true)
          return false;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<KeyboardEvent>(
        KEY_TAB_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }
          event.preventDefault();
          return editor.dispatchCommand(INSERT_TAB_COMMAND, undefined);
        },
        COMMAND_PRIORITY_EDITOR,
      )
    );
  }, [editor, $updateToolbar, updateToolbarState]);

  return (
    <div className="toolbar" ref={toolbarRef}>
      {props.type == "basic" && (<>
        <DropDown
          disabled={false}
          buttonClassName='toolbar-item spaced'
          buttonTitle='Heading'
          buttonLabel={blockTypeToBlockName[toolbarState.blockType]}
          buttonIcon={<RiArrowDownSLine />}
        >
          <DropDownItem
            className={
              'item wide ' + dropDownActiveClass(toolbarState.blockType === 'paragraph')
            }
            onClick={() => formatParagraph(editor)}>
            <div className="icon-text-container">
              <span className="text">Paragraph</span>
            </div>
          </DropDownItem>
          <DropDownItem
            className={'item wide ' + dropDownActiveClass(toolbarState.blockType === 'h1')}
            onClick={() => formatHeading(editor, toolbarState.blockType, 'h1')}>
            <div className="icon-text-container">
              <span className="text editor-heading-h1">Heading 1</span>
            </div>
          </DropDownItem>
          <DropDownItem
            className={'item wide ' + dropDownActiveClass(toolbarState.blockType === 'h2')}
            onClick={() => formatHeading(editor, toolbarState.blockType, 'h2')}>
            <div className="icon-text-container">
              <span className="text editor-heading-h2">Heading 2</span>
            </div>
          </DropDownItem>
          <DropDownItem
            className={'item wide ' + dropDownActiveClass(toolbarState.blockType === 'h3')}
            onClick={() => formatHeading(editor, toolbarState.blockType, 'h3')}>
            <div className="icon-text-container">
              <span className="text editor-heading-h3">Heading 3</span>
            </div>
          </DropDownItem>

        </DropDown>
        <DropDown disabled={false} buttonClassName="toolbar-item spaced" buttonTitle="Line Height" buttonAriaLabel="Insert specialized editor node" buttonIcon={<RiLineHeight />}>
          {lineHeights.map((option) => {
            return (<DropDownItem
              className={'item wide ' + dropDownActiveClass(toolbarState.lineHeight === option)}
              onClick={() => {
                editor.update(() => {
                  let paragraphNodes = $findAllParagraphsInSelection($getSelection());
                  paragraphNodes?.forEach((pNode) => {
                    if (pNode instanceof CustomParagraphNode) {
                      pNode.setLineHeight(option);
                    }
                  })
                })
              }}
              key={option}>
              <div className="icon-text-container">
                <span className="text">{option}</span>
              </div>
            </DropDownItem>)
          })}
        </DropDown>
        <button
          title="Bold"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          }}
          className={'toolbar-item spaced ' + (toolbarState.isBold ? 'active' : '')}
          aria-label="Format Bold"
        >
          <RiBold />
        </button>
        <button
          title="Italic"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
          className={'toolbar-item spaced ' + (toolbarState.isItalic ? 'active' : '')}
          aria-label="Format Italics"
        >
          <RiItalic />
        </button>
        <button
          title="Underline"
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          }}
          className={'toolbar-item spaced ' + (toolbarState.isUnderline ? 'active' : '')}
          aria-label="Format Underline"
        >
          <RiUnderline />
        </button>

        <DropDown
          disabled={false}
          stopCloseOnClickSelf={true}
          buttonTitle='Font Color'
          buttonClassName="toolbar-item spaced"
          buttonAriaLabel="Insert specialized editor node"
          isOpen={fontColorDropDownOpen}
          handleToggle={(newFontColorDropdownOpen) => { setFontColorDropDownOpen(newFontColorDropdownOpen) }}
          buttonIcon={<RiFontColor color={toolbarState.fontColor} />}>
          <ColorPicker
            currentColor={toolbarState.fontColor}
            close={() => { setFontColorDropDownOpen(false) }}
            applyColor={(color) => { setFontColorDropDownOpen(false); applyStyleText({ color: color }, false) }}
          />
        </DropDown>

        <Divider />
      </>)}
      <button
        title="Undo"
        disabled={!toolbarState.canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        className="toolbar-item spaced"
        aria-label="Undo">
        <RiArrowGoBackFill />
      </button>
      <button
        title="Redo"
        disabled={!toolbarState.canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        className="toolbar-item"
        aria-label="Redo">
        <RiArrowGoForwardFill />
      </button>
      {props.showEquationEditorButton && <button
        className="toolbar-item insert-equation"
        aria-label="Insert Equation"
        title="Insert Equation"
        onClick={() => { updateToolbarState("equationEditorIsOpen", !toolbarState.equationEditorIsOpen) }}
      >
        <RiFormula /> <span className="text">Insert Equation</span>
      </button>}
      {
        toolbarState.equationEditorIsOpen &&
        props.equationEditorContainerElement &&
        createPortal(
          <EquationEditorPopup
            close={() => { updateToolbarState("equationEditorIsOpen", false); updateToolbarState("equationEditorEditNode", null); }}
            editNode={toolbarState.equationEditorEditNode}
            writeEquation={(svg, latex, editNode) => {
              if (editNode) {
                editor.update(() => {
                  editNode.replace(new EquationNode(svg, latex))
                })
              } else {
                editor.dispatchCommand(INSERT_EQUATION_COMMAND, {
                  latex: latex,
                  svg: svg
                })
              }
              updateToolbarState("equationEditorEditNode", null);
              updateToolbarState("equationEditorIsOpen", false)
            }} />
          , props.equationEditorContainerElement)
      }
    </div>
  );
}